import * as React from 'react';
import { defaultMemoize } from 'reselect';
import * as _ from 'underscore';

import { IFilterDefinition, ISelectFilterDefinition, ISelectFilterDefinitionOption } from '~content/services/FilterPanelService';
import { AuthenticationProviderType, IAuthenticationProvider, SiteAuthenticationSettingsService } from '~src/settings/siteAuthSettings/services/SiteAuthenticationSettingsService';
import { FeatureFlags } from '~src/ts/FeatureFlags';
import { AuthenticationFilterHelper } from '~src/ts/helpers/AuthenticationFilterHelper';

import { SiteUserDefinition } from '../../../contentDefinitions/SiteUserDefinition';
import { ServerFilterClauses } from '../../../ts/helpers/ServerFilterClauses';
import { SiteRoleFilterHelper } from '../../../ts/helpers/SiteRoleFilterHelper';
import { IStringValuedFilterClause } from '../../../ts/ServerApi/types/IFilterClause';
import { SiteRoles } from '../../../ts/ServerApi/types/ISiteRole';
import { SiteRolesResource } from '../../../userManagement/services/resources/SiteRoles';
import { SiteUsers } from '../../../userManagement/services/resources/SiteUsers';
import { isGroupSpace } from '../../state/SpaceInfo';
import { AdvancedFilterPanel, AdvancedFilterPanelProps } from './AdvancedFilterPanel';


export interface SiteUsersAdvancedFilterPanelProps extends AdvancedFilterPanelProps {}

export interface SiteUsersAdvancedFilterPanelState {
  siteRoleOptionsWithCounts: ISelectFilterDefinitionOption<string>[];
  authenticationAvailableOptions: ISelectFilterDefinitionOption<string>[];
}

// This function is a weird way to update filter definitions asynchronously
// since those definitions are created in the child component - AdvancedFilterPanel.
// See related comment there.
function transformFilterDefinitions(
  siteRoleOptionsWithCounts: ISelectFilterDefinitionOption<string>[],
  authenticationAvailableOptions: ISelectFilterDefinitionOption<string>[],
  authenticationFilterOnUsersPageEnabled: boolean,
): (filterDefinitions: IFilterDefinition<any>[]) => void {
  return (filterDefinitions: IFilterDefinition<any>[]) => {
    if (siteRoleOptionsWithCounts) {
      (filterDefinitions[0] as ISelectFilterDefinition<string>).options = siteRoleOptionsWithCounts;
    }

    if(authenticationFilterOnUsersPageEnabled && authenticationAvailableOptions){
      (filterDefinitions[1] as ISelectFilterDefinition<string>).options = authenticationAvailableOptions;
    }
  };
}

export class SiteUsersAdvancedFilterPanel extends React.Component<SiteUsersAdvancedFilterPanelProps, SiteUsersAdvancedFilterPanelState> {

  private isUnmounted = false;
  private siteRoles: SiteRolesResource = undefined;
  private memoizedTransformFilterDefinitions = defaultMemoize(transformFilterDefinitions);

  public constructor(props: SiteUsersAdvancedFilterPanelProps) {
    super(props);
    this.siteRoles = SiteRolesResource.instance;
    this.state = {
      siteRoleOptionsWithCounts: null,
      authenticationAvailableOptions: null
    };
  }

  public render() {
    const transformFilterDefinitions = this.memoizedTransformFilterDefinitions(
      this.state.siteRoleOptionsWithCounts,
      this.state.authenticationAvailableOptions,
      FeatureFlags.authenticationFilterOnUsersPageEnabled
    );
    return <AdvancedFilterPanel {...this.props} transformFilterDefinitions={transformFilterDefinitions} />;
  }

  public componentDidMount() {
    this.updateSiteRoleCounts();
    if(FeatureFlags.authenticationFilterOnUsersPageEnabled) {
      this.updateAuthenticationType();
    }
  }

  public componentDidUpdate(prevProps: SiteUsersAdvancedFilterPanelProps) {
    if (this.searchTerm(this.props) !== this.searchTerm(prevProps)) {
      this.updateSiteRoleCounts();
    }
  }

  public componentWillUnmount() {
    this.isUnmounted = true;
  }

  private updateAuthenticationType = async () => {
    const authenticationTypesOnSite = await this.getAllProvidersOnSite();
    const authenticationOptions = AuthenticationFilterHelper.getOptionsForSite(authenticationTypesOnSite);
    !this.isUnmounted && this.setState({authenticationAvailableOptions: authenticationOptions});
  }

  private async getAllProvidersOnSite(): Promise<string[]> {
    const siteAuthenticationSettings = await SiteAuthenticationSettingsService.getSiteAuthenticationSettings();
    let authTypes: string[] = [];
    if(siteAuthenticationSettings.defaultProvider.isEnabled) {
      authTypes.push(AuthenticationProviderType.default);
    }
    if(siteAuthenticationSettings.tabIdMfaProvider.isEnabled){
      authTypes.push(AuthenticationProviderType.tabidmfa);
    }
    const externalProviders = siteAuthenticationSettings.externalProviders;
    if(externalProviders.length > 0) {
        _.each(externalProviders, (externalProvider: IAuthenticationProvider) => {
          if(externalProvider.isEnabled) {
            authTypes.push(externalProvider.type);
          }
        });
    }
    return authTypes;
  }

  private updateSiteRoleCounts = async () => {
    let query = SiteUserDefinition.getBasicQuery();
    if (isGroupSpace(this.props.spaceInfo)) {
      query = query.addFilter((ServerFilterClauses.serverFilterClauses.inGroup(this.props.spaceInfo.item.id)));
    }
    if (this.searchTerm(this.props)) {
      query = query.addFilter(this.props.mixedContentFilterMap.search);
    }
    const userCountsByRole = await SiteUsers.instance.getUserCountsBySiteRole(query);

    let siteRolesToExclude: string[];
    if (FeatureFlags.hideServerAdminsFromUsersPageEnabled) {
      siteRolesToExclude = [SiteRoles.Guest, SiteRoles.ServerAdministrator];
    } else {
      siteRolesToExclude = [SiteRoles.Guest];
    }
    const siteRoleOptionsWithCounts = SiteRoleFilterHelper.siteRoleOptions(
      _.without(this.siteRoles.allOptions(), ...siteRolesToExclude),
      userCountsByRole
    );

    !this.isUnmounted && this.setState({ siteRoleOptionsWithCounts });
  }

  private searchTerm = (props: SiteUsersAdvancedFilterPanelProps) => {
    return (props.mixedContentFilterMap.search
      && (props.mixedContentFilterMap.search as IStringValuedFilterClause).value)
      || '';
  }
}
